"use client";

import { Brush, Pen, Star, GraduationCap, Phone, Users } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CuratedBeautyServices() {
  const header = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  const services = [{
    code: "[ 01 ]",
    title: "Bridal Artistry",
    description: "Bespoke makeup design tailored to your unique features, ensuring a radiant, long-lasting glow for your wedding day.",
    Icon: Brush
  }, {
    code: "[ 02 ]",
    title: "Editorial Styling",
    description: "High-fashion, trend-forward makeup looks crafted for photoshoots, campaigns, and creative editorial projects.",
    Icon: Pen
  }, {
    code: "[ 03 ]",
    title: "Special Event Glam",
    description: "Sophisticated styling for galas, red carpet appearances, or intimate celebrations where you need to shine.",
    Icon: Star
  }, {
    code: "[ 04 ]",
    title: "Makeup Lessons",
    description: "Personalized one-on-one sessions to master professional techniques, product selection, and daily application.",
    Icon: GraduationCap
  }, {
    code: "[ 05 ]",
    title: "Bridal Party Packages",
    description: "Coordinated beauty services for your entire bridal party, ensuring a cohesive and elegant aesthetic for your day.",
    Icon: Users
  }, {
    code: "[ 06 ]",
    title: "Consultation Sessions",
    description: "In-depth pre-wedding trials to refine your vision and ensure every detail is perfect before you walk down the aisle.",
    Icon: Phone
  }];
  return <section data-section-id="994" id="curated-beauty-services" className="bg-background text-foreground py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div>
            <p className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6">[ SERVICES ]</p>
            <h2 className="font-serif font-default text-foreground leading-[1.05] tracking-tight" style={{
            fontSize: "clamp(2.5rem, 5vw, 4rem)"
          }}>
              Artistry that defines,{" "}
              <em className="italic font-serif text-primary">your natural glow.</em>
            </h2>
          </div>
          <div className="flex flex-col justify-end gap-6">
            <p className="text-base lg:text-lg text-muted-foreground leading-relaxed max-w-md">
              From ethereal bridal looks to sharp editorial precision, Joanne Glow Studio 
              delivers bespoke beauty experiences designed to celebrate your unique essence.
            </p>
            <Link to="/SignatureExperiencesPage" className="group inline-flex items-center gap-2 font-mono text-sm text-foreground hover:text-primary transition-colors w-fit">
              <span className="border-b border-foreground/30 group-hover:border-primary pb-0.5">
                View all services
              </span>
              <span className="transition-transform group-hover:translate-x-1">
                &rarr;
              </span>
            </Link>
          </div>
        </div>

        
        <div className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <div key={i} data-index={i} className="group relative flex flex-col bg-card border border-border rounded-lg p-10 transition-all duration-300 hover:border-primary overflow-hidden">
                
                <div className="mb-10">
                  <Icon className="text-primary transition-transform duration-300 group-hover:scale-105" strokeWidth={1} style={{
                width: "64px",
                height: "64px"
              }} />
                </div>

                
                <p className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-4">
                  {service.code}
                </p>

                
                <h3 className="font-serif font-default text-2xl text-card-foreground leading-tight mb-4">
                  {service.title}
                </h3>

                
                <p className="font-default text-[15px] leading-relaxed text-muted-foreground mb-8">
                  {service.description}
                </p>

                
                <div className="mt-auto pt-6 border-t border-border">
                  <Link to="/SignatureExperiencesPage" className="inline-flex items-center gap-1.5 font-mono text-xs text-primary hover:gap-2.5 transition-all">
                    <span>Learn more</span>
                    <span>&rarr;</span>
                  </Link>
                </div>

                
                <span className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full transition-all duration-500 ease-out" style={{
              backgroundColor: "var(--primary)"
            }} aria-hidden="true" />
              </div>;
        })}
        </div>

        
        <div className="mt-12 flex justify-center lg:hidden">
          <Button asChild variant="outline" size="lg" className="font-mono text-sm rounded-lg">
            <Link to="/SignatureExperiencesPage">View all services &rarr;</Link>
          </Button>
        </div>
      </div>
    </section>;
}